import { useCallback, useEffect, useState } from "preact/hooks";

import {
  archiveContact as archiveContactApi,
  createContact as createContactApi,
  fetchContacts,
  patchContact as apiPatchContact,
  restoreContact as restoreContactApi,
  type CreateContactBody,
  type PatchContactBody,
} from "../lib/api";
import type { ContactOut } from "../types";

/** Name-ascending, client-side (ADR-002 A6 decision 8.1 — the endpoint
 * carries no guaranteed server-side order, this is the ratified,
 * permanent client behavior, not a stopgap). */
function sortContactsByName(rows: ContactOut[]): ContactOut[] {
  return [...rows].sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Sprint-010 C4 (design-phase-c.md §4, ADR-002 Amendment 6 decision 8) —
 * the Contacts page's data. `GET /api/v1/contacts` is admin/manager/api
 * (`canHandOutWork`) — a member gets a 403, so `enabled` here IS a real
 * role gate (same posture as `useProjects`, not `useAnimals`'s open-to-
 * everyone shape) — the caller must pass `canHandOutWork` so a member
 * session never even issues this request.
 *
 * Sprint-013 A3/C3 (design-spec-animal-cards-2026-07-10.md §5, SA ruling
 * sa-ruling-crud-endpoints-2026-07-10.md Decision 3, ADR-002 Amendment 8) —
 * `refresh()` always fetches WITH archived rows included
 * (`fetchContacts(true)`), so `ContactsView`'s Active/All toggle can filter
 * client-side with no second request — the exact same "fetch everything
 * once, filter locally" shape `useProjects`/`ProjectsView`'s toggle already
 * established. Also adds the Add/Edit/Archive/Restore CRUD flow.
 */
export function useContacts(enabled: boolean) {
  const [contacts, setContacts] = useState<ContactOut[] | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!enabled) return;
    setError(null);
    try {
      const rows = await fetchContacts(true);
      setContacts(sortContactsByName(rows));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn't load the contacts list.");
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Sprint-013 C3 (design-spec-animal-cards-2026-07-10.md §5) — the "+ Add"
  // sheet's submit. Inserts the created row directly into local state (kept
  // name-sorted, same §8 TD-010-class rule `useProjects.createProject`
  // follows) — no `refresh()` call, so the list never remounts/flashes on
  // add.
  const createContact = useCallback(async (input: Omit<CreateContactBody, "idempotency_key">) => {
    const created = await createContactApi({ idempotency_key: crypto.randomUUID(), ...input });
    setContacts((prev) => sortContactsByName(prev ? [...prev, created] : [created]));
    return created;
  }, []);

  // The contact detail's Edit sheet submit, admin/api only. Patches the ONE
  // changed row directly into local state (the full `updated` row —
  // `ContactPatchOut` already carries every `ContactOut` field), re-sorted
  // in case the name changed — no `refresh()` call, same §8 TD-010-class
  // rule `useProjects.patchProject` follows.
  const patchContact = useCallback(
    async (contactId: string, updates: Pick<PatchContactBody, "name" | "contact" | "notes">) => {
      const updated = await apiPatchContact(contactId, {
        idempotency_key: crypto.randomUUID(),
        ...updates,
      });
      setContacts((prev) =>
        prev ? sortContactsByName(prev.map((c) => (c.id === contactId ? updated : c))) : prev,
      );
      return updated;
    },
    [],
  );

  // Sprint-013 A3/C3 (SA ruling Decision 3.4) — the Archive flow's
  // `DELETE /contacts/{id}`. `ContactArchiveOut` only carries
  // `id`/`archived_at`/`replayed` — patch just that one field into local
  // state, in place, same §8 TD-010-class rule `useProjects.archiveProject`
  // follows.
  const archiveContact = useCallback(async (contactId: string) => {
    const result = await archiveContactApi(contactId, crypto.randomUUID());
    setContacts((prev) =>
      prev
        ? prev.map((c) => (c.id === contactId ? { ...c, archived_at: result.archived_at } : c))
        : prev,
    );
    return result;
  }, []);

  // Sprint-013 A3/C3 (SA ruling Decision 3.4) — the Restore flow's
  // `POST /contacts/{id}/restore`. `ContactRestoreOut` carries the full row
  // (like `patchContact`'s response) — patch it in place wholesale.
  const restoreContact = useCallback(async (contactId: string) => {
    const updated = await restoreContactApi(contactId, crypto.randomUUID());
    setContacts((prev) => (prev ? prev.map((c) => (c.id === contactId ? updated : c)) : prev));
    return updated;
  }, []);

  return {
    contacts,
    loading,
    error,
    refresh,
    createContact,
    patchContact,
    archiveContact,
    restoreContact,
  };
}
